import { z } from 'zod';

export const runtimeQuestionSchema = z.object({
  id: z.number(),
  key: z.string(),
  label: z.string(),
  question_type: z.enum(['string', 'radio', 'text', 'select', 'multi_select']),
  required: z.boolean(),
  placeholder: z.string().optional(),
  order: z.number().optional(),
  max_selections: z.number().optional(),
  options: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .optional(),
});

export type RuntimeQuestion = z.infer<typeof runtimeQuestionSchema>;

export const createQuestionResponseValidation = (question: RuntimeQuestion) => {
  switch (question.question_type) {
    case 'string':
      return question.required
        ? z.string().min(1, 'This field is required')
        : z.string().optional();
    case 'text':
      return question.required
        ? z
            .string()
            .min(1, 'This field is required')
            .min(10, 'Please provide more detail (minimum 10 characters)')
        : z.string().optional();
    case 'radio':
    case 'select': {
      const validOptions =
        question.options?.map((option) => option.value) || [];
      return question.required
        ? z
            .string()
            .min(1, 'Please make a selection')
            .refine((val) => validOptions.includes(val), 'Invalid selection')
        : z.string().optional();
    }
    case 'multi_select': {
      const validMultiOptions = question.options?.map((opt) => opt.value) || [];

      const baseArray = question.required
        ? z.array(z.string()).min(1, 'Please select at least one option')
        : z.array(z.string());

      const validatedArray = baseArray
        .refine(
          (values) => values.every((val) => validMultiOptions.includes(val)),
          'Invalid selection'
        )
        .refine(
          (values) =>
            !question.max_selections ||
            values.length <= question.max_selections,
          question.max_selections
            ? `Please select at most ${question.max_selections} options`
            : 'Too many selections'
        );

      return question.required ? validatedArray : validatedArray.optional();
    }
    default:
      return z.string().optional();
  }
};

export const createSectionFormSchema = (questions: RuntimeQuestion[]) => {
  const schemaShape: Record<string, z.ZodTypeAny> = {};

  questions.forEach((question) => {
    schemaShape[question.key] = createQuestionResponseValidation(question);
  });

  return z.object(schemaShape);
};
